'use client';

import type { MouseEvent } from 'react';

import Link from 'next/link';

import { cn } from '@ps/ui/utils';

import {
  contentTabItemClassName,
  contentTabItemStyle,
} from '../functions/contentTabItemStyle';
import type { ContentTabStripStyle } from '../functions/view-models/contentTabStripStyle';
import type { ContentTabViewModel } from '../types/ContentTabViewModel';

interface Props {
  tab: ContentTabViewModel;
  /** Resolved strip style (colors + font). */
  strip: ContentTabStripStyle;
  /** This tab is the active sub-route. */
  isActive: boolean;
  className?: string;
}

/**
 * Selecting a content tab means "go to this tab's top" — it must NOT carry a
 * stale interior-linking fragment (e.g. `#collections/<id>` from a shortcut jump)
 * onto the tab route. Next 16.2.x doesn't clear it on its own: a same-pathname
 * `<Link>` click is a no-op that leaves the hash, and a cross-tab click can carry
 * it (issues #93126 / #91658). So on a plain click we strip the CURRENT URL's
 * fragment via the router-integrated History API BEFORE the `<Link>` navigates —
 * clearing the current hash (rather than writing the destination) leaves the tab
 * route to load normally while guaranteeing no fragment survives. `history.state`
 * is preserved so Back isn't frozen; modifier / non-primary clicks
 * (open-in-new-tab) are left untouched.
 */
function clearStaleFragment(event: MouseEvent<HTMLAnchorElement>): void {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    window.location.hash === ''
  ) {
    return;
  }
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${window.location.search}`,
  );
}

/**
 * Visitor content-tab item — a Next `<Link>` to the tab's sub-route
 * (`/<urlCode>/<kind>`). Because the bio chrome + the `@body` parallel slot live
 * in one shared layout, clicking soft-navigates and swaps ONLY the `@body` slot
 * in place — the chrome (header / profile card / strip) is not re-rendered and
 * the page never reloads. `<Link>` still renders a crawlable `<a href>`, so the
 * strip stays SEO-friendly. Owners get the separate
 * {@link EditableContentTabStrip} button variant instead.
 */
export function ContentTabLink({ tab, strip, isActive, className }: Props) {
  return (
    <Link
      href={tab.href}
      role="tab"
      aria-selected={isActive}
      onClick={clearStaleFragment}
      className={cn(contentTabItemClassName, className)}
      style={contentTabItemStyle({ strip, isActive })}
    >
      {tab.title}
    </Link>
  );
}
